import { ref } from "vue";
import type { AuthProvider, AuthProviderConfig } from "@ics/auth-api";
import { EntraProvider } from "./EntraProvider";
import { B2CProvider } from "./B2CProvider";
import { TestAuthProvider } from "./TestAuthProvider";
import { getEnvironmentVariables, type ConfigCache } from "@/helpers/auth";
import { syncSessionStores } from "./sessionSync";

const debugPrefix = "[AuthShell]";
const ACTIVE_PROVIDER_KEY = "ics_active_provider_id";

// Reaktive refs slik at Vue-komponenter (login-UX) re-renderes når init
// fullfører. Eksponeres internt via getProviders/getActiveProvider for at
// useAuthShell-composable skal kunne bygge ComputedRefs som faktisk reagerer.
const providersRef = ref<AuthProvider[]>([]);
const activeProviderRef = ref<AuthProvider | null>(null);
let initPromise: Promise<void> | null = null;

interface ConfigCacheWithProviders extends ConfigCache {
  authProviders?: AuthProviderConfig[];
  AuthProviders?: AuthProviderConfig[];
}

function readActiveProviderIdFromStorage (): string | null {
  try {
    return localStorage.getItem(ACTIVE_PROVIDER_KEY);
  } catch {
    return null;
  }
}

function writeActiveProviderIdToStorage (id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(ACTIVE_PROVIDER_KEY);
    } else {
      localStorage.setItem(ACTIVE_PROVIDER_KEY, id);
    }
  } catch {
    // localStorage utilgjengelig (private browsing / kvote) — ignorer
  }
}

function buildProvider (config: AuthProviderConfig): AuthProvider | null {
  if (config.type === "entra") {
    return new EntraProvider(config);
  }
  if (config.type === "b2c") {
    try {
      return new B2CProvider(config);
    } catch (error) {
      console.error(`${debugPrefix} Kunne ikke bygge B2CProvider '${config.id}':`, error);
      return null;
    }
  }
  // WI 2488: isolert E2E-testinnlogging. Averteres kun av /api/config bak dobbeltgaten, så denne grenen
  // nås aldri i test/prod.
  if (config.type === "e2e") {
    return new TestAuthProvider(config);
  }
  console.warn(`${debugPrefix} Ukjent provider-type '${config.type}'; hopper over '${config.id}'`);
  return null;
}

function deriveLegacyEntraConfig (config: ConfigCache): AuthProviderConfig | null {
  if (!config.VITE_MSAL_CLIENT_ID || !config.VITE_MSAL_AUTHORITY) {
    return null;
  }

  let scopes: string[];
  if (Array.isArray(config.VITE_MSAL_SCOPES)) {
    scopes = config.VITE_MSAL_SCOPES;
  } else if (typeof config.VITE_MSAL_SCOPES === "string" && config.VITE_MSAL_SCOPES.trim().length > 0) {
    const scopeStr = config.VITE_MSAL_SCOPES.trim();
    scopes = scopeStr.startsWith("[") ? JSON.parse(scopeStr) : scopeStr.split(/\s+/).filter(s => s.length > 0);
  } else {
    scopes = [`api://${config.VITE_MSAL_CLIENT_ID}/.default`];
  }

  return {
    id: "entra",
    displayName: "Microsoft Entra ID",
    type: "entra",
    clientId: config.VITE_MSAL_CLIENT_ID,
    authority: config.VITE_MSAL_AUTHORITY,
    scopes
  };
}

async function loadProviderConfigs (): Promise<AuthProviderConfig[]> {
  const config = await getEnvironmentVariables() as ConfigCacheWithProviders;

  // Backenden returnerer authProviders-array (#1196). Aksepter både camelCase og PascalCase,
  // siden API-en ennå normaliserer feltene blandet (jf. helpers/auth.ts:225-269).
  const fromBackend = config.authProviders ?? config.AuthProviders;
  if (Array.isArray(fromBackend) && fromBackend.length > 0) {
    return fromBackend;
  }

  // Fallback for eldre BE/test-miljøer som ikke har authProviders-array ennå.
  const derived = deriveLegacyEntraConfig(config);
  return derived ? [derived] : [];
}

/**
 * Initialiser shell. Idempotent — kan kalles flere ganger uten effekt etter første.
 * - Bygger provider-instanser fra config.
 * - Velger aktiv provider basert på persisted `activeProviderId` (silent restore),
 *   ellers første registrerte provider.
 */
export function initProviderRegistry (): Promise<void> {
  initPromise ??= (async () => {
    const configs = await loadProviderConfigs();
    const built = configs
      .map(buildProvider)
      .filter((p): p is AuthProvider => p !== null);
    providersRef.value = built;
    console.log(`${debugPrefix} Initialisert med ${built.length} provider(e):`, built.map(p => p.id));

    if (built.length === 0) {
      console.warn(`${debugPrefix} Ingen providers registrert — auth-shell tom`);
      return;
    }

    const persistedId = readActiveProviderIdFromStorage();
    const persisted = persistedId ? built.find(p => p.id === persistedId) ?? null : null;
    activeProviderRef.value = persisted ?? built[0];
    if (!persisted && persistedId) {
      // Persisted id refererer en provider som ikke lenger er registrert; rydd opp.
      writeActiveProviderIdToStorage(null);
    }
  })();
  return initPromise;
}

export function getProviders (): readonly AuthProvider[] {
  return providersRef.value;
}

export function getActiveProvider (): AuthProvider | null {
  return activeProviderRef.value;
}

export function setActiveProvider (id: string): AuthProvider {
  const next = providersRef.value.find(p => p.id === id);
  if (!next) {
    throw new Error(`Ingen provider med id '${id}' er registrert`);
  }
  activeProviderRef.value = next;
  writeActiveProviderIdToStorage(id);
  return next;
}

/** Logger ut aktiv provider; andre providers' caches berøres ikke. */
export async function logoutActiveProvider (): Promise<void> {
  const active = activeProviderRef.value;
  if (!active) {
    return;
  }
  await active.logout();
  // Behold activeProvider-pekeren slik at neste login forblir på samme provider
  // med mindre brukeren eksplisitt velger en annen via setActiveProvider().
}

/**
 * Kjør deterministisk konto-restore på aktiv provider før router monteres.
 * Idempotent og feilsikker — eventuelle feil swallow-es med warn-log slik at
 * router-guarden fortsatt får ruse videre til Login når restore feiler.
 * Brukes av `main.js` mellom `initProviderRegistry()` og `app.use(router)`.
 */
export async function warmRestoreSession (): Promise<boolean> {
  const active = activeProviderRef.value;
  if (!active) {
    return false;
  }

  try {
    if (await active.restore()) {
      return true;
    }
  } catch (error) {
    console.warn(`${debugPrefix} warmRestoreSession feilet for aktiv provider '${active.id}':`, error);
  }

  // WI 2291: aktiv provider (sist valgte) har ingen gjenopprettbar sesjon — men
  // brukeren kan ha en gyldig sesjon under en annen provider. Prøv de øvrige.
  // Nøyaktig én treff → bytt aktiv dit (sømløs gjenoppretting). Flere treff →
  // overlat valget til provider-velgeren (returnér false → router-guarden viser
  // Login med begge knappene) i stedet for å gjette identitet.
  const others = providersRef.value.filter(p => p.id !== active.id);
  const restored: AuthProvider[] = [];
  for (const provider of others) {
    try {
      if (await provider.restore()) {
        restored.push(provider);
      }
    } catch (error) {
      console.warn(`${debugPrefix} warmRestoreSession fallback feilet for '${provider.id}':`, error);
    }
  }

  if (restored.length === 1) {
    setActiveProvider(restored[0].id);
    return true;
  }
  return false;
}

/**
 * Håndter redirect-respons ved oppstart på AKTIV provider (WI 2113). Uten dette
 * ble bare legacy-Entra-instansens redirect-svar plukket opp i `main.js`, og en
 * B2C-redirect-retur ble liggende uhåndtert — med hengende
 * `interaction_in_progress`-state som blokkerte alle senere påloggingsforsøk.
 *
 * Cold-boot-kortslutning (WI 1218): uten auth-respons-indikatorer i URL-en er
 * det garantert ingenting å håndtere — `handleRedirectPromise` kan henge taust
 * ved cold boot og må ikke kalles ubetinget.
 *
 * Feilsikker: provider-feil swallow-es med warn slik at oppstarten alltid
 * fortsetter til router-mount.
 */
export async function handleShellRedirectResponse (): Promise<string | null> {
  const active = activeProviderRef.value;
  if (!active) {
    return null;
  }

  const hasAuthResponse = /[&#?](code|state|id_token|access_token|error|error_description)=/
    .test(`${window.location.hash}${window.location.search}`);
  if (!hasAuthResponse) {
    return null;
  }

  try {
    const route = await active.handleRedirectResponse();
    if (route !== null) {
      // Redirect-stien synket ikke shared stores (kun popup-stien gjorde det),
      // så B2C-redirect-retur lot UI vise påloggingsvalgene (WI 2196). Synk her
      // sentralt — providers holdes Pinia-frie.
      syncSessionStores(active.currentAccount());
    }
    return route;
  } catch (error) {
    console.warn(`${debugPrefix} handleShellRedirectResponse feilet for provider '${active.id}':`, error);
    return null;
  }
}

/**
 * Test-only: nullstiller intern state slik at neste init starter på nytt.
 * Ikke ment for produksjon.
 */
export function _resetProviderRegistryForTests (): void {
  providersRef.value = [];
  activeProviderRef.value = null;
  initPromise = null;
}
